import { useMutation } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { awsWafTokenHeaders } from "lib/auth/awsWafToken";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";

/**
 * Kicks off the better-auth password-reset flow: the auth-service emails the user a reset link that
 * lands on the /auth/reset-password page with a one-time token.
 *
 * We deliberately don't pass `redirectTo`: the auth-service builds the reset link itself from
 * APP_ROOT_URL (not from better-auth's `data.url`), so any `redirectTo` sent here is ignored.
 * @returns A mutation object with methods to trigger the request and track its status.
 */
export function useRequestPasswordReset() {
  return useMutation({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["requestPasswordReset"],
    mutationFn: async ({ email }: { email: string }) => {
      // Anti-bot: attach a WAF token to this request (see awsWafToken.ts).
      const wafHeaders = await awsWafTokenHeaders();
      const { data, error } = await authClient.requestPasswordReset(
        { email },
        { headers: wafHeaders },
      );
      if (error) {
        throw new Error(betterAuthErrorText(error, "Could not send password reset email"), {
          cause: error,
        });
      }
      return data;
    },
  });
}
